import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Minus, Plus, Loader2, Upload, FileText } from "lucide-react";
import logo from '../../assets/images/cup-artie.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useOnboarding } from "@/context/OnboardingContext";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";

export default function NewProjectStep2() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { data, updateData } = useOnboarding();
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Check if we are resuming an existing project from URL params
    const resumeProjectId = searchParams.get('projectId');
    const isNewProject = location.state?.isNewProject;

    // Project Details State - Initialize empty if resuming, otherwise use context
    // We want to force API load for resume to avoid stale localStorage data
    const [projectTitle, setProjectTitle] = useState(isNewProject || resumeProjectId ? "" : data.projectTitle || "");
    const [projectType, setProjectType] = useState(isNewProject || resumeProjectId ? "" : data.projectType || "");
    const [budget, setBudget] = useState(isNewProject || resumeProjectId ? "" : data.budget || "");
    const [logline, setLogline] = useState(isNewProject || resumeProjectId ? "" : data.logline || "");
    const [episodeCount, setEpisodeCount] = useState<number | "">(isNewProject || resumeProjectId ? 1 : data.episodeCount || 1);

    // Script Upload State
    const [file, setFile] = useState<File | null>(isNewProject || resumeProjectId ? null : data.scriptFile || null);
    const [uploadedScript, setUploadedScript] = useState<{ id: string, name: string } | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Checkbox State
    const [sameAsPrior, setSameAsPrior] = useState(isNewProject || resumeProjectId ? false : data.sameAsPrior || false);

    const [isSaving, setIsSaving] = useState(false);
    const [isBackSaving, setIsBackSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Resume Logic: Fetch project data if ID is in URL
    // Also fetch if we have a projectId in context but are not "new" (handling Back button without URL param case)
    useEffect(() => {
        const targetId = resumeProjectId || (!isNewProject && data.projectId);

        if (targetId) {
            setIsLoading(true);
            const fetchData = async () => {
                try {
                    const [project, scripts] = await Promise.all([
                        apiClient.getProject(targetId),
                        apiClient.getScriptsByProject(targetId)
                    ]);

                    if (project) {
                        setProjectTitle(project.title || "");

                        // Parse format back to type/count
                        if (project.format) {
                            if (project.format.includes("episode")) {
                                const match = project.format.match(/^(.*?) \((\d+) episodes?\)$/);
                                if (match) {
                                    setProjectType(match[1]);
                                    setEpisodeCount(parseInt(match[2]));
                                } else {
                                    setProjectType(project.format);
                                }
                            } else {
                                setProjectType(project.format);
                            }
                        }

                        setLogline(project.logline || "");
                        setSameAsPrior(project.same_as_prior || false);

                        // Map brand_revenue_target (number) back to budget string
                        if (project.brand_revenue_target) {
                            const target = project.brand_revenue_target;
                            let budgetStr = "";

                            // Feature film / Other options
                            if (project.format === "Feature film" || project.format === "Other") {
                                if (target <= 300000) budgetStr = "Under $300K";
                                else if (target <= 700000) budgetStr = "$300K – $700K";
                                else if (target <= 2000000) budgetStr = "$700K – $2M";
                                else if (target <= 5000000) budgetStr = "$2M – $5M";
                                else budgetStr = "$5M+";
                            }
                            // Short film options
                            else if (project.format === "Short film") {
                                if (target <= 50000) budgetStr = "Under $50K";
                                else if (target <= 300000) budgetStr = "$50K – $300K";
                                else if (target <= 700000) budgetStr = "$300K – $700K";
                                else budgetStr = "$700K+";
                            }
                            // Episodic options (Long-form)
                            else if (project.format && project.format.includes("Long-form")) {
                                if (target <= 50000) budgetStr = "Under $50K";
                                else if (target <= 300000) budgetStr = "$50K – $300K";
                                else if (target <= 700000) budgetStr = "$300K – $700K";
                                else if (target <= 2000000) budgetStr = "$700K – $2M";
                                else budgetStr = "$2M+";
                            }
                            // Vertical short options
                            else if (project.format && project.format.includes("Vertical")) {
                                if (target <= 20000) budgetStr = "Under $20K";
                                else if (target <= 50000) budgetStr = "$20K – $50K";
                                else budgetStr = "$50K+";
                            }

                            if (budgetStr) {
                                setBudget(budgetStr);
                            }
                        }

                        // Update context so subsequent steps have the ID
                        updateData({
                            projectId: project._id,
                            projectTitle: project.title,
                            logline: project.logline || ""
                        });
                    }

                    if (scripts && scripts.length > 0) {
                        const script = scripts[0];
                        setUploadedScript({ id: script.id, name: script.filename });
                        // CRITICAL: Ensure context has the script ID
                        updateData({
                            scriptId: script.id,
                            scriptFileName: script.filename
                        });
                    }
                } catch (error) {
                    console.error("Failed to resume project:", error);
                    toast.error("Failed to load project details");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchData();
        }
    }, [resumeProjectId]);

    // Reset logic for "New Project" button
    useEffect(() => {
        if (isNewProject) {
            updateData({
                projectId: undefined,
                scriptId: undefined,
                scriptFile: null,
                scriptFileName: null,
                projectTitle: "",
                projectType: "",
                budget: "",
                logline: "",
                episodeCount: 1,
                sameAsPrior: false
            });
            // Clear local state
            setProjectTitle("");
            setProjectType("");
            setBudget("");
            setLogline("");
            setEpisodeCount(1);
            setFile(null);
            setSameAsPrior(false);
            setUploadedScript(null);

            window.history.replaceState({}, document.title);
            sessionStorage.removeItem("project_connecting_youtube");
        }
    }, [isNewProject]);

    // Only sync from context if NOT a new project AND NOT resuming (standard flow)
    // AND we haven't already loaded data from API (to prevent overwriting fresh API data with stale context)
    useEffect(() => {
        // If we are loading from API, ignore context sync
        const targetId = resumeProjectId || (!isNewProject && data.projectId);
        if (targetId) return;

        if (!isNewProject) {
            if (data.projectTitle !== projectTitle && data.projectTitle) setProjectTitle(data.projectTitle);
            if (data.scriptId && data.scriptFileName && !data.scriptFile && !uploadedScript) {
                setUploadedScript({ id: data.scriptId, name: data.scriptFileName });
            }
        }
    }, [data, isNewProject, resumeProjectId, uploadedScript]); // Added uploadedScript dep

    // File Handling
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) validateAndSetFile(selectedFile);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) validateAndSetFile(droppedFile);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const validateAndSetFile = (file: File) => {
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        const validExtensions = ['pdf', 'docx', 'txt'];

        if (!validExtensions.includes(fileExtension || '')) {
            toast.error("Invalid file format. Please upload PDF, DOCX, or TXT.");
            return;
        }

        setFile(file);
        setUploadedScript(null); // Clear uploaded script if new file selected
    };


    const parseBudget = (str: string, projectType: string = "") => {
        if (!str || str === "I'm not sure") {
            switch (projectType) {
                case "Vertical short": return 35000;
                case "Long-form episodic": return 175000;
                case "Short film": return 175000;
                case "Feature film":
                case "Other":
                default: return 500000;
            }
        }
        const matches = str.match(/(\d+(?:\.\d+)?)\s*[kKmM]?/g);
        if (!matches) return 0;
        let maxVal = 0;
        matches.forEach(m => {
            const num = parseFloat(m);
            if (isNaN(num)) return;
            let multiplier = 1;
            const lower = m.toLowerCase();
            if (lower.includes('k')) multiplier = 1000;
            if (lower.includes('m')) multiplier = 1000000;
            const val = num * multiplier;
            if (val > maxVal) maxVal = val;
        });
        return maxVal;
    };

    const saveProject = async (isBack = false) => {
        if (isBack) setIsBackSaving(true);
        else setIsSaving(true);

        try {
            let pid = data.projectId;
            const budgetValue = parseBudget(budget, projectType);

            let formatValue = projectType;
            const finalEpisodeCount = typeof episodeCount === 'number' ? episodeCount : 1;
            if (projectType === "Long-form episodic" || projectType === "Vertical short") {
                formatValue = `${projectType} (${finalEpisodeCount} episode${finalEpisodeCount !== 1 ? 's' : ''})`;
            }

            // 1. Create or Update Project
            if (pid) {
                await apiClient.updateProject(pid, {
                    title: projectTitle,
                    format: formatValue,
                    episode_count: finalEpisodeCount,
                    brand_revenue_target: budgetValue,
                    logline: logline,
                    same_as_prior: sameAsPrior
                });
            } else {
                const projectPayload = {
                    title: projectTitle,
                    status: "draft",
                    brand_revenue_target: budgetValue,
                    target_30d_reach: 0,
                    launch_date: new Date().toISOString().split('T')[0],
                    created_via_onboarding: false,
                    format: formatValue,
                    episode_count: finalEpisodeCount,
                    logline: logline,
                    same_as_prior: sameAsPrior
                };
                const createdProject = await apiClient.createProject(projectPayload);
                // @ts-ignore
                pid = createdProject.id || createdProject._id;
            }

            if (!pid) throw new Error("Failed to save project");

            // 2. Handle File Upload (If new file selected)
            // We upload it immediately to ensure persistence, but WITHOUT scoring
            let sid = data.scriptId;
            if (file) {
                const scriptResult = await apiClient.uploadScript({
                    projectId: pid,
                    file: file,
                    title: file.name,
                    and_calculate_score: false // Critical: Do not trigger analysis yet
                });
                // @ts-ignore
                sid = scriptResult.script_id || scriptResult.id || scriptResult._id;

                // Update local state to reflect upload
                setFile(null);
                setUploadedScript({ id: sid || "", name: file.name });
            }

            // 3. Update Context
            updateData({
                projectId: pid,
                scriptId: sid,
                scriptFile: null, // Clear file from context since it's uploaded
                scriptFileName: file?.name || uploadedScript?.name || data.scriptFileName,
                projectTitle,
                projectType,
                budget,
                logline,
                episodeCount: finalEpisodeCount,
                sameAsPrior
            });

            // Invalidate projects query to ensure Dashboard is fresh when returning
            queryClient.invalidateQueries({ queryKey: ["projects"] });

            return true;
        } catch (error) {
            console.error("Save error:", error);
            toast.error("Failed to save project. Please try again.");
            return false;
        } finally {
            setIsSaving(false);
            setIsBackSaving(false);
        }
    };

    const handleNext = async () => {
        if (!projectTitle.trim()) {
            toast.error("Please enter a project title");
            return;
        }
        if (!projectType) {
            toast.error("Please select a project type");
            return;
        }
        if (!budget) {
            toast.error("Please enter a target budget");
            return;
        }
        if (!file && !uploadedScript && !data.scriptId) {
            toast.error("Please upload a script to continue");
            return;
        }

        const success = await saveProject(false);
        if (success) {
            if (sameAsPrior) {
                navigate('/projects/create/step-3', { replace: true });
            } else {
                navigate('/projects/create/audience', { replace: true });
            }
        }
    };

    const handleBack = async () => {
        // Auto-save draft on back if title exists
        if (projectTitle.trim()) {
            await saveProject(true);
            toast.success("Draft saved");
        }
        navigate('/dashboard');
    };

    const projectTypes = [
        { label: "Vertical short", description: "Serialized. TikTok, Reels, Shorts, etc." },
        { label: "Long-form episodic", description: "Serialized. YouTube, Netflix, Disney+, Roku, etc." },
        { label: "Feature film", description: "Standalone, 40+ minutes. Theatrical, festivals, or streaming." },
        { label: "Short film", description: "Standalone, under 40 minutes. Festivals, YouTube, or streaming." },
        { label: "Other", description: "" }
    ];

    const getBudgetConfig = () => {
        switch (projectType) {
            case "Vertical short":
                return { question: "What's your budget per episode?", options: ["Under $20K", "$20K – $50K", "$50K+", "I'm not sure"] };
            case "Long-form episodic":
                return { question: "What's your budget per episode?", options: ["Under $50K", "$50K – $300K", "$300K – $700K", "$700K – $2M", "$2M+", "I'm not sure"] };
            case "Short film":
                return { question: "What's your project budget?", options: ["Under $50K", "$50K – $300K", "$300K – $700K", "$700K+", "I'm not sure"] };
            case "Feature film":
            case "Other":
            default:
                return { question: "What's your project budget?", options: ["Under $300K", "$300K – $700K", "$700K – $2M", "$2M – $5M", "$5M+", "I'm not sure"] };
        }
    };

    const budgetConfig = getBudgetConfig();

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">
            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

                <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header Section */}
                    <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            Tell me a bit about <br />your project
                        </h1>
                        <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2">
                            This helps me understand what you are building and how to read your script. Keep it simple. You can refine this later.
                        </p>
                    </div>

                    {/* Main Form Container */}
                    <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden animate-in slide-in-from-bottom-4 duration-500 delay-100 p-8 md:p-10">

                        {/* Privacy Disclaimer */}
                        <div className="mb-8 bg-[oklch(0.98_0_0)] rounded-lg px-4 py-3 flex items-center gap-3 border border-[oklch(0.95_0_0)]">
                            <ShieldCheck className="w-4 h-4 text-[oklch(0.55_0_0)] flex-shrink-0" />
                            <p className="text-xs text-[oklch(0.55_0_0)] leading-tight">
                                This information is used only to analyze and improve your project. You can update or change it later.
                            </p>
                        </div>


                        <div className="space-y-8">
                            {/* Project Title */}
                            <div className="space-y-2">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    Project Title <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Enter title..."
                                    value={projectTitle}
                                    onChange={(e) => setProjectTitle(e.target.value)}
                                    className="bg-white border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base"
                                />
                                <p className="text-xs text-[oklch(0.55_0_0)]">This can be a working title.</p>
                            </div>

                            {/* Project Type */}
                            <div className="space-y-3">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    What kind of project is this? <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {projectTypes.map((type) => (
                                        <button
                                            key={type.label}
                                            onClick={() => {
                                                setProjectType(type.label);
                                                setBudget("");
                                            }}
                                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${projectType === type.label
                                                ? "bg-[oklch(0.2686_0_0)] text-white border-[oklch(0.2686_0_0)]"
                                                : "bg-white text-[oklch(0.55_0_0)] border-[oklch(0.92_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                }`}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                                {projectType && (
                                    <p className="text-xs text-[oklch(0.55_0_0)] animate-in fade-in slide-in-from-top-1 duration-200">
                                        {projectTypes.find(t => t.label === projectType)?.description || ""}
                                    </p>
                                )}
                            </div>

                            {/* Episodic Series Counter */}
                            {(projectType === "Long-form episodic" || projectType === "Vertical short") && (
                                <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                        How many episodes are you thinking?
                                    </label>
                                    <div className="flex items-center">
                                        <div className="flex items-center border border-[oklch(0.92_0_0)] rounded-md bg-white">
                                            <button
                                                onClick={() => setEpisodeCount(Math.max(1, (typeof episodeCount === 'number' ? episodeCount : 1) - 1))}
                                                className="w-12 h-12 flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-[oklch(0.98_0_0)] transition-colors rounded-l-full border-r border-[oklch(0.92_0_0)]"
                                                disabled={(typeof episodeCount === 'number' ? episodeCount : 1) <= 1}
                                            >
                                                <Minus className="w-4 h-4" />
                                            </button>
                                            <input
                                                type="number"
                                                min={1}
                                                value={episodeCount}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "") {
                                                        setEpisodeCount("");
                                                    } else {
                                                        const num = parseInt(val);
                                                        if (!isNaN(num)) {
                                                            setEpisodeCount(Math.max(1, num));
                                                        }
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (episodeCount === "" || episodeCount < 1) {
                                                        setEpisodeCount(1);
                                                    }
                                                }}
                                                className="w-16 h-12 flex items-center justify-center text-center font-bold text-lg text-[oklch(0.2686_0_0)] font-cabinet border-none focus:ring-0 outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent"
                                            />
                                            <button
                                                onClick={() => setEpisodeCount((typeof episodeCount === 'number' ? episodeCount : 1) + 1)}
                                                className="w-12 h-12 flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-[oklch(0.98_0_0)] transition-colors rounded-r-full border-l border-[oklch(0.92_0_0)]"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Target Budget */}
                            <div className="space-y-3">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    {budgetConfig.question} <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {budgetConfig.options.map((opt) => (
                                        <button
                                            key={opt}
                                            onClick={() => setBudget(opt)}
                                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${budget === opt
                                                ? "bg-[oklch(0.2686_0_0)] text-white border-[oklch(0.2686_0_0)]"
                                                : "bg-white text-[oklch(0.55_0_0)] border-[oklch(0.92_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-[oklch(0.55_0_0)]">This is directional. It helps me size opportunities, not lock anything in.</p>
                            </div>

                            {/* Logline */}
                            <div className="space-y-2">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    What is your logline?<span className="text-[oklch(0.55_0_0)] font-normal text-sm ml-1">(optional)</span>
                                </label>
                                <Textarea
                                    placeholder="One or two sentences is perfect. What is the story, tone, or idea?"
                                    value={logline}
                                    onChange={(e) => setLogline(e.target.value)}
                                    className="min-h-[120px] bg-white border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] focus-visible:ring-0 focus-visible:border-[oklch(0.70_0.18_55)] text-base resize-none"
                                />
                                <p className="text-xs text-[oklch(0.55_0_0)]">This gives me context beyond the script itself.</p>
                            </div>

                            {/* Script Upload Section (Added) */}
                            <div className="space-y-4 pt-4 border-t border-[oklch(0.92_0_0)]">
                                <div>
                                    <h2 className="text-2xl font-bold mb-2 font-cabinet text-[oklch(0.2686_0_0)]">Upload your script <span className="text-red-500">*</span></h2>
                                    <p className="text-[oklch(0.55_0_0)] mb-4 text-sm font-ibm">
                                        Scripts give us the most to work with. Pilot, episode, full season, feature — whatever you have.
                                    </p>
                                </div>

                                {!file && !uploadedScript ? (
                                    <div
                                        className={`w-full border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 ${isDragging
                                            ? "border-[oklch(0.70_0.18_55)] bg-[oklch(0.97_0.01_60)]/20"
                                            : "border-[oklch(0.75_0_0)] hover:bg-[oklch(0.98_0_0)] hover:border-[oklch(0.70_0.18_55)]/50"
                                            }`}
                                        onClick={() => fileInputRef.current?.click()}
                                        onDrop={handleDrop}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                    >
                                        <input
                                            type="file"
                                            className="hidden"
                                            ref={fileInputRef}
                                            accept=".pdf,.docx,.txt"
                                            onChange={handleFileSelect}
                                        />
                                        <div className="w-12 h-12 bg-[oklch(0.98_0_0)] rounded-full flex items-center justify-center mb-3 shadow-sm">
                                            <Upload className="w-6 h-6 text-[oklch(0.2686_0_0)]" />
                                        </div>
                                        <p className="text-lg font-bold mb-1 text-[oklch(0.2686_0_0)]">Drop script here or Click Upload</p>
                                        <p className="text-xs text-gray-400">PDF, DOCX, TXT (Max 180 pages)</p>
                                    </div>
                                ) : (
                                    <div className="w-full border-2 border-[oklch(0.92_0_0)] bg-[oklch(0.98_0_0)] rounded-xl p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 bg-white rounded-lg border border-[oklch(0.92_0_0)] flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-[oklch(0.70_0.18_55)]" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-[oklch(0.2686_0_0)] truncate max-w-[180px] sm:max-w-xs text-sm">
                                                    {file ? file.name : uploadedScript?.name || "Uploaded Script"}
                                                </p>
                                                {file ? (
                                                    <p className="text-xs text-[oklch(0.55_0_0)]">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                                ) : (
                                                    <p className="text-xs text-green-600 font-medium">✓ Uploaded</p>
                                                )}
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setFile(null);
                                                setUploadedScript(null);
                                            }}
                                            className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8"
                                        >
                                            Change
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {/* Prior Project Checkbox (Added) */}
                            <div className="flex items-start space-x-3 pt-2">
                                <Checkbox
                                    id="same-as-prior"
                                    checked={sameAsPrior}
                                    onCheckedChange={(checked) => setSameAsPrior(checked === true)}
                                    className="mt-1"
                                />
                                <div className="grid gap-1.5 leading-none">
                                    <label
                                        htmlFor="same-as-prior"
                                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                    >
                                        This project shares the same writer and distribution as my prior project
                                    </label>
                                    <p className="text-xs text-muted-foreground">
                                        Checking this will skip the audience setup.
                                    </p>
                                </div>
                            </div>

                        </div>
                    </Card>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-6 md:px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={handleBack}
                                disabled={isSaving || isBackSaving || isLoading}
                            >
                                {isBackSaving ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <ArrowLeft className="w-4 h-4 mr-2" />
                                )}
                                Back
                            </Button>
                        </div>

                        {/* Progress Indicators */}
                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={isSaving || isBackSaving || isLoading || !projectTitle.trim() || !projectType || !budget || (!file && !uploadedScript && !data.scriptId)}
                            >
                                Continue
                                {isSaving ? (
                                    <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}